import type { ApiResponse, ErrorCode } from '@outfalak/shared'
import { EmbeddedSessionError, sessionToken } from './shopify.js'

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

/**
 * The failure the UI branches on.
 *
 * `code` is the stable machine-readable one from the API's envelope, so a
 * screen can react to `billing.wallet_insufficient` with a top-up prompt rather
 * than string-matching a sentence that changes the next time somebody edits it.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  is(code: ErrorCode | string): boolean {
    return this.code === code
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, BASE)

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = options.body ? { 'content-type': 'application/json' } : {}

  /*
   * Inside the Shopify admin the cookie below does not exist — it is
   * third-party there and the browser drops it — so the credential is a fresh
   * App Bridge session token instead. Fetched per request, because Shopify
   * issues them with a sixty-second life.
   *
   * `credentials: 'include'` is left on regardless. It costs nothing when there
   * is no cookie to send, and making it conditional would mean one more way for
   * the standalone dashboard to lose its session.
   */
  let bearer: string | null

  try {
    bearer = await sessionToken()
  } catch (cause) {
    /*
     * Embedded, and Shopify would not give us a session token. The request is
     * abandoned rather than sent: `credentials:'include'` below would otherwise
     * carry the standalone dashboard's cookie out of the admin iframe, and the
     * API would answer as whoever that cookie belongs to. Silently showing a
     * merchant somebody else's account is worse than any error message.
     */
    if (cause instanceof EmbeddedSessionError) {
      throw new ApiError(
        0,
        'shopify.session_unavailable',
        'This app could not start a session with Shopify. Close it and reopen it from your Shopify admin.',
      )
    }

    throw cause
  }

  if (bearer) headers.authorization = `Bearer ${bearer}`

  let response: Response

  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      // The session cookie is on a different host from the dashboard, so every
      // call is credentialed. Forgetting this on one call is the bug that looks
      // like "randomly signed out".
      credentials: 'include',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal ?? null,
    })
  } catch (cause) {
    // A network failure is not a 500 and must not read like one — the merchant's
    // connection dropped, and telling them the server broke sends them to
    // support instead of to their wifi.
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.', undefined)
  }

  let payload: ApiResponse<T>

  try {
    payload = (await response.json()) as ApiResponse<T>
  } catch {
    throw new ApiError(response.status, 'bad_response', `The server returned something unreadable (${response.status}).`)
  }

  if (!payload.ok) {
    // `error` is guaranteed by the envelope, but this is the branch that runs
    // when something *else* answered — a proxy error page that happens to be
    // JSON, or an endpoint whose `ok` means something other than "the request
    // succeeded". Reaching through an absent object here would throw a
    // TypeError from inside the client, which reads as a frontend crash rather
    // than as the server problem it is.
    if (!payload.error) {
      throw new ApiError(response.status, 'bad_response', `The server returned something unreadable (${response.status}).`)
    }

    throw new ApiError(response.status, payload.error.code, payload.error.message, payload.error.fields)
  }

  return payload.data
}

export const apiBase = BASE

// ── Health ──────────────────────────────────────────────────────────────────

export interface Health {
  /** False when the request never got an answer at all. */
  reachable: boolean
  /** The API's own verdict: every dependency it checks is up. */
  healthy: boolean
  postgres: 'up' | 'down' | 'unknown'
  redis: 'up' | 'down' | 'unknown'
  realtime: { connections: number; channels: number }
  version: string | null
}

const UNREACHABLE: Health = {
  reachable: false,
  healthy: false,
  postgres: 'unknown',
  redis: 'unknown',
  realtime: { connections: 0, channels: 0 },
  version: null,
}

/**
 * `/health`, read outside the envelope contract on purpose.
 *
 * That endpoint's `ok` answers "is the platform healthy", not "did this request
 * succeed" — the two words collide, and a degraded API returns `ok: false` with
 * a perfectly good `data` block and no `error` beside it. Put through `api()`
 * that reads as a failed request and throws away the very payload the caller
 * asked for, which is backwards: the one moment a status panel has to render is
 * the moment something is down.
 *
 * This never throws. An unreachable API is a health answer too, and it is the
 * one the merchant most needs a screen for.
 */
export async function health(signal?: AbortSignal): Promise<Health> {
  let payload: { ok?: boolean; data?: Partial<Health> & { realtime?: { connections?: number; channels?: number } } }

  try {
    const response = await fetch(new URL('/health', BASE), { credentials: 'include', signal: signal ?? null })
    payload = (await response.json()) as typeof payload
  } catch {
    return UNREACHABLE
  }

  const data = payload.data
  if (!data) return UNREACHABLE

  return {
    reachable: true,
    healthy: payload.ok === true,
    postgres: data.postgres === 'up' || data.postgres === 'down' ? data.postgres : 'unknown',
    redis: data.redis === 'up' || data.redis === 'down' ? data.redis : 'unknown',
    realtime: {
      connections: Number(data.realtime?.connections ?? 0),
      channels: Number(data.realtime?.channels ?? 0),
    },
    version: typeof data.version === 'string' ? data.version : null,
  }
}
